import { useMemo, useState } from "react";
import { Image as ImageIcon } from "lucide-react";
import {
  exportPdfPagesAsImages,
  type ImageExportFormat,
} from "../lib/pdfOps";
import { saveAndShareBytes } from "../lib/shareText";
import { ToolPageShell } from "./ToolPages";

type Props = {
  /** PDF buffer — required for PDF export. */
  buffer: ArrayBuffer | null;
  pageCount: number;
  kind: "pdf" | "docx";
  /** For DOCX: capture visible reader root. */
  docRoot?: HTMLElement | null;
  onClose: () => void;
  onMinimize?: () => void;
};

function parseRange(input: string, max: number): number[] {
  const raw = input.trim();
  if (!raw || raw === "*" || raw === "all" || raw === "همه") {
    return Array.from({ length: max }, (_, i) => i + 1);
  }
  const set = new Set<number>();
  for (const part of raw.split(/[,،\s]+/)) {
    const m = part.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (m) {
      let a = Number(m[1]);
      let b = Number(m[2]);
      if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
      if (a > b) [a, b] = [b, a];
      for (let n = a; n <= b; n++) {
        if (n >= 1 && n <= max) set.add(n);
      }
      continue;
    }
    const n = Number(part);
    if (Number.isFinite(n) && n >= 1 && n <= max) set.add(n);
  }
  return [...set].sort((a, b) => a - b);
}

async function captureDomPages(
  root: HTMLElement,
  format: ImageExportFormat,
  quality: number,
  scale: number
): Promise<{ bytes: Uint8Array; fileName: string; mime: ImageExportFormat }[]> {
  const pages = root.querySelectorAll<HTMLElement>(
    ".docx-page, .docx-sheet, .nr-docx"
  );
  const targets =
    pages.length > 0 ? Array.from(pages).slice(0, 40) : [root];
  const results: {
    bytes: Uint8Array;
    fileName: string;
    mime: ImageExportFormat;
  }[] = [];
  const ext = format === "image/png" ? "png" : "jpg";

  for (let i = 0; i < targets.length; i++) {
    const el = targets[i]!;
    const rect = el.getBoundingClientRect();
    const w = Math.max(1, Math.ceil(rect.width * scale));
    const h = Math.max(1, Math.ceil(rect.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
        <foreignObject width="100%" height="100%">
          <div xmlns="http://www.w3.org/1999/xhtml" style="width:${rect.width}px;transform:scale(${scale});transform-origin:top left;background:#fff;">
            ${new XMLSerializer().serializeToString(el.cloneNode(true))}
          </div>
        </foreignObject>
      </svg>`;
    const url = URL.createObjectURL(
      new Blob([svg], { type: "image/svg+xml;charset=utf-8" })
    );
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("رندر تصویر ناموفق"));
        image.src = url;
      });
      ctx.drawImage(img, 0, 0);
    } catch {
      URL.revokeObjectURL(url);
      continue;
    }
    URL.revokeObjectURL(url);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(
        (b) => resolve(b),
        format,
        format === "image/jpeg" ? quality : undefined
      )
    );
    if (!blob) continue;
    const ab = await blob.arrayBuffer();
    results.push({
      bytes: new Uint8Array(ab),
      fileName: `nevisign-image-${i + 1}.${ext}`,
      mime: format,
    });
  }
  if (!results.length) {
    throw new Error(
      "تبدیل ورد به تصویر در این دستگاه پشتیبانی نشد. فایل را به PDF تبدیل کنید."
    );
  }
  return results;
}

export function ToImageSheet({
  buffer,
  pageCount,
  kind,
  docRoot,
  onClose,
  onMinimize,
}: Props) {
  const [format, setFormat] = useState<ImageExportFormat>("image/jpeg");
  const [range, setRange] = useState("همه");
  const [quality, setQuality] = useState(0.85);
  const [scale, setScale] = useState(1.5);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  const total = Math.max(1, pageCount || 1);
  const selectedCount = useMemo(
    () => parseRange(range, total).length,
    [range, total]
  );

  const run = async () => {
    setBusy(true);
    setHint(null);
    try {
      if (kind === "pdf") {
        if (!buffer || buffer.byteLength === 0) {
          throw new Error("فایل PDF در دسترس نیست.");
        }
        const pages = parseRange(range, total);
        if (!pages.length) throw new Error("صفحه‌ای انتخاب نشده است.");
        const images = await exportPdfPagesAsImages(buffer, pages, {
          format,
          quality,
          scaleFactor: scale,
          namePrefix: "nevisign-image",
        });
        for (const img of images) {
          await saveAndShareBytes(img.bytes, img.fileName, img.mime);
        }
        setHint(
          `${images.length.toLocaleString("fa-IR")} تصویر آماده اشتراک شد.`
        );
      } else {
        if (!docRoot) throw new Error("سند ورد برای تبدیل آماده نیست.");
        const images = await captureDomPages(docRoot, format, quality, scale);
        for (const img of images) {
          await saveAndShareBytes(img.bytes, img.fileName, img.mime);
        }
        setHint(
          `${images.length.toLocaleString("fa-IR")} تصویر از سند ورد آماده شد.`
        );
      }
    } catch (err) {
      setHint(err instanceof Error ? err.message : "خطا در تبدیل");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ToolPageShell
      title={kind === "pdf" ? "تبدیل PDF به تصویر" : "تبدیل به تصویر"}
      toolId="to-image"
      description={
        kind === "pdf"
          ? `PDF با ${total.toLocaleString("fa-IR")} صفحه — بازه یا همه را انتخاب کنید.`
          : "صفحات نمایش‌داده‌شدهٔ ورد به تصویر تبدیل می‌شوند."
      }
      onClose={onClose}
      onMinimize={onMinimize}
      busy={busy}
      footer={
        <button
          type="button"
          className="primary-btn"
          disabled={busy}
          onClick={() => void run()}
        >
          <ImageIcon size={16} />
          {busy ? "…" : "ذخیره / اشتراک"}
        </button>
      }
    >
      {kind === "pdf" && (
        <label className="settings-hint" style={{ display: "block" }}>
          صفحات (مثلاً ۱-۳ یا همه)
          <input
            value={range}
            onChange={(e) => setRange(e.target.value)}
            style={{ display: "block", width: "100%", marginTop: "0.35rem" }}
            placeholder="همه"
          />
        </label>
      )}
      <div className="to-image-row">
        <label>
          فرمت
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as ImageExportFormat)}
          >
            <option value="image/jpeg">JPG</option>
            <option value="image/png">PNG</option>
          </select>
        </label>
        {format === "image/jpeg" && (
          <label>
            کیفیت ({Math.round(quality * 100)}٪)
            <input
              type="range"
              min={0.4}
              max={1}
              step={0.05}
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
            />
          </label>
        )}
        <label>
          اندازه ({scale.toFixed(1)}×)
          <input
            type="range"
            min={0.5}
            max={3}
            step={0.1}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
          />
        </label>
      </div>
      {kind === "pdf" && (
        <p className="settings-hint">
          {selectedCount.toLocaleString("fa-IR")} صفحه انتخاب شده
        </p>
      )}
      {hint && <p className="settings-hint">{hint}</p>}
    </ToolPageShell>
  );
}
